/* eslint-disable no-irregular-whitespace */
import { priceFor } from '@brands/Dashboard/Dashboard/Patient/utils/helpers';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { CaseType } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectOrganization } from '@brands/store/selectors/organization';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import {
  getPatientReviewProfileRoleSegment,
  getStaffReviewProfileBasePath,
  isEndUserPatientRole,
  isStaffCaseCreationRole,
} from '@brands/Utils/reviewProfileBasePath';
import { Button } from '@mui/material';
import React, { FC, useCallback } from 'react';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import Modal, { Styles } from 'react-modal';

import styles from './styles.module.scss';

export type VerificationType =
  | 'verify'
  | 'notListed'
  | 'notCovered_Yes'
  | 'notCovered_No'
  | 'notCovered_Unsure'
  | 'invalid_Yes'
  | 'invalid_No'
  | 'invalid_Unsure'
  | 'valid_Yes'
  | 'valid_No'
  | 'valid_Unsure';

interface InsuranceVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  verificationType: VerificationType;
  callAddOtherCarrierIdIsSelected: () => void;
  /** When set (e.g. collector payment before provider), continue intake instead of confirm-payment. */
  intakeContinueRoute?: string;
  intakeContinueState?: Record<string, unknown>;
}

const mobileCustomStylesDashboard: Styles = {
  ...customStylesDashboard,
  overlay: {
    ...customStylesDashboard.overlay,
    overflowY: 'scroll' as const,
  },
  content: {
    ...customStylesDashboard.content,
    transform: `translate(-50%, -40%)`,
    width: '90%',
    padding: '32px',
    inset: '45% auto auto 50%',
  },
};

const customStylesDashboardDesktop: Styles = {
  ...customStylesDashboard,
  overlay: {
    ...customStylesDashboard.overlay,
  },
  content: {
    ...customStylesDashboard.content,
    flexDirection: 'column' as const,
    width: 'auto',
    maxWidth: '444px',
    padding: '48px 48px 32px 48px',
    overflowY: 'auto' as const,
    maxHeight: '850px',
    justifyContent: 'flex-start' as const,
    backgroundColor: '#fff',
    borderRadius: '4px',
    minHeight: 'unset',
  },
};

const InsuranceVerificationModal: FC<InsuranceVerificationModalProps> = ({
  isOpen,
  onClose,
  verificationType,
  callAddOtherCarrierIdIsSelected,
  intakeContinueRoute,
  intakeContinueState,
}) => {
  const isMobile = useWindowSize().width < 992;
  const { values } = useAppSelector(selectPatientForm);
  const roleNavigate = useRoleNavigate();
  const { userInfo } = useAppSelector(selectAuth);
  const { currentOrganization } = useAppSelector(selectOrganization);
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const caseType = (values.find((item) => item.form_question_id === 20)?.value.caseType as string) || '';
  const currentOrganizationPrices = useAppSelector(selectOrganizationPrices);
  const p = priceFor(currentOrganizationPrices.currentOrganizationPrices, caseType as CaseType);
  const shouldHideSelfPay =
    p?.payment_options && p?.payment_options?.length === 1 && p?.payment_options?.includes('Insurance');
  const continueFunction = (): void => {
    if (intakeContinueRoute) {
      roleNavigate(intakeContinueRoute, { state: intakeContinueState });
      return;
    }
    roleNavigate('/confirm-payment');
  };

  const goToDashboard = useCallback(() => {
    if (isStaffCaseCreationRole(userInfo.role.name) && currentPatient?.id != null) {
      roleNavigate(
        `${getStaffReviewProfileBasePath(userInfo.role.name)}/${getPatientReviewProfileRoleSegment(
          currentPatient,
        )}/${currentPatient.id}`,
      );
    } else {
      roleNavigate('/dashboard');
    }
  }, [userInfo.role.name, roleNavigate, currentPatient]);

  const renderContent = (): JSX.Element | null => {
    switch (verificationType) {
      case 'verify':
        return (
          <>
            <h2 className={`fs-unmask ${styles.modalHeader}`} data-testid="insurance-verification-header">
              Checking your insurance details
            </h2>
            <p className={`fs-unmask ${styles.modalBody}`}>
              Please wait while we check your benefit details. This may take up to a minute.
            </p>
            <span className={`fs-unmask ${styles.spinner}`} />
          </>
        );
      case 'notListed':
        return (
          <>
            <h2 className={`fs-unmask ${styles.modalHeader}`} data-testid="insurance-verification-header">
              We are unable to verify your insurance
            </h2>
            <p className={`fs-unmask ${styles.modalBody}`}>
              Since your selected insurance is not listed, we’ll need to collect your credit card information. You can
              also reach our Care Advisor Team at <a href="tel:844-240-1883">844-240-1883</a>.
            </p>
          </>
        );
      case 'notCovered_Yes':
      case 'invalid_Yes':
        return (
          <>
            <h2 className={`fs-unmask ${styles.modalHeader}`} data-testid="insurance-verification-header">
              We are unable to verify your insurance
            </h2>
            <p className={`fs-unmask ${styles.modalBody}`}>
              It might just be a small issue with the information you entered.
            </p>
            <p className={`fs-unmask ${styles.modalBody}`}>
              You can try again or if you need help you can reach out to our{' '}
              <a href="https://dentistryone.zendesk.com/hc/en-us" target="_blank" rel="noopener noreferrer">
                support team
              </a>{' '}
              or call us at <a href="tel:844-240-1883">844-240-1883</a>.
            </p>
            <Button
              data-testid="insurance-verification-try-again"
              variant="contained"
              className={`fs-unmask ${styles.verificationContinue}`}
              onClick={onClose}
            >
              Try Again
            </Button>
            <Button
              variant="text"
              data-testid="insurance-verification-return-to-dashboard"
              className={`fs-unmask ${styles.verificationSelfPay}`}
              onClick={() => goToDashboard()}
            >
              Return to Dashboard
            </Button>
          </>
        );
      case 'notCovered_No':
      case 'notCovered_Unsure':
      case 'invalid_No':
      case 'invalid_Unsure':
        return (
          <>
            <h2 className={`fs-unmask ${styles.modalHeader}`} data-testid="insurance-verification-header">
              We are unable to verify your insurance
            </h2>
            <p className={`fs-unmask ${styles.modalBody}`}>
              Your plan may not cover this service. Please reach out to our{' '}
              <a href="https://dentistryone.zendesk.com/hc/en-us" target="_blank" rel="noopener noreferrer">
                support team
              </a>{' '}
              or call us at <a href="tel:844-240-1883">844-240-1883</a> if you need help.
            </p>
            <span className={styles.modalBodySpan}>To continue, you’ll need to add a payment method.</span>
            <Button
              variant="contained"
              className={`fs-unmask ${styles.verificationContinue}`}
              onClick={() => callAddOtherCarrierIdIsSelected()}
              data-testid="insurance-verification-self-pay"
            >
              Self Pay
            </Button>
          </>
        );
      case 'valid_Yes':
        return (
          <>
            <h2 className={`fs-unmask ${styles.modalHeader}`} data-testid="insurance-verification-header">
              Your insurance is verified
            </h2>
            <p className={`fs-unmask ${styles.modalBody}`}>
              You’re all set! This service is covered at no cost to you.
            </p>
            <Button
              variant="contained"
              data-testid="insurance-verification-continue"
              className={`fs-unmask ${styles.verificationContinue}`}
              onClick={() => continueFunction()}
            >
              Continue
            </Button>
          </>
        );
      case 'valid_No':
      case 'valid_Unsure':
        return (
          <>
            <h2 className={`fs-unmask ${styles.modalHeader}`} data-testid="insurance-verification-header">
              Your insurance is confirmed
            </h2>
            <p className={`fs-unmask ${styles.modalBody}`}>
              Your estimated cost today is $0. We’ll contact you if there’s a remaining balance due to your
              deductible and/or coinsurance. The most you’ll pay is $100.
            </p>
            {!shouldHideSelfPay && (
              <p className={`fs-unmask ${styles.modalBody}`}>You can also choose to pay out of pocket instead.</p>
            )}
            <Button
              variant="contained"
              data-testid="insurance-verification-use-insurance"
              className={`fs-unmask ${styles.verificationContinue}`}
              onClick={continueFunction}
            >
              Use Insurance
            </Button>
            {!shouldHideSelfPay && (
              <Button
                variant="text"
                className={`fs-unmask ${styles.verificationSelfPay}`}
                onClick={() => callAddOtherCarrierIdIsSelected()}
                data-testid="insurance-verification-self-pay"
              >
                Self Pay
              </Button>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={isMobile ? mobileCustomStylesDashboard : customStylesDashboardDesktop}
      ariaHideApp={false}
      contentLabel="Insurance Verification Modal"
    >
      <button type="button" className={styles.openCasesCloseBtn} onClick={onClose}>
        <AiOutlineCloseCircle style={{ fontSize: '24px' }} />
      </button>
      <div className={styles.openCasesContainer}>{renderContent()}</div>
    </Modal>
  );
};

Modal.setAppElement('#root');
export default InsuranceVerificationModal;
